import { create } from "zustand";
import type { ItemFormData } from "./itemFormData";

/**
 * A per-item editing overlay: the server snapshot the draft was opened
 * against, plus only the values the user changed.
 */
export interface ItemDraft {
  baseline: ItemFormData;
  values: Partial<ItemFormData>;
}

/** Form keys holding whole record objects, reconciled per sub-key
 * (priceModelInputs: only while the draft keeps the baseline's model). */
type RecordFieldKey = "customFields" | "priceModelInputs";

interface ItemDraftsStore {
  draftsByItemId: Map<string, ItemDraft>;
  ensureDraft: (itemId: string, serverForm: ItemFormData) => void;
  setDraftValue: <K extends keyof ItemFormData>(
    itemId: string,
    key: K,
    value: ItemFormData[K],
  ) => void;
  rebaseDraft: (itemId: string, serverForm: ItemFormData) => void;
  clearDraft: (itemId: string) => void;
}

/**
 * Store-backed item drafts, keyed by item id. Drafts survive editor and
 * panel unmounts; dirty state is simply which keys a draft holds.
 */
export const useItemDraftsStore = create<ItemDraftsStore>((set, get) => ({
  draftsByItemId: new Map(),

  ensureDraft: (itemId, serverForm) => {
    if (get().draftsByItemId.has(itemId)) {
      return;
    }
    set((state) => {
      const draftsByItemId = new Map(state.draftsByItemId);
      draftsByItemId.set(itemId, { baseline: serverForm, values: {} });
      return { draftsByItemId };
    });
  },

  setDraftValue: (itemId, key, value) => {
    set((state) => {
      const draft = state.draftsByItemId.get(itemId);
      if (!draft) {
        return {};
      }
      const values = { ...draft.values };
      if (isSameValue(value, draft.baseline[key])) {
        delete values[key];
      } else {
        values[key] = value;
      }
      const draftsByItemId = new Map(state.draftsByItemId);
      draftsByItemId.set(itemId, { ...draft, values });
      return { draftsByItemId };
    });
  },

  rebaseDraft: (itemId, serverForm) => {
    set((state) => {
      const draft = state.draftsByItemId.get(itemId);
      if (!draft) {
        return {};
      }
      const values = pruneMatchingValues(draft.values, serverForm);
      // Record keys rebase per sub-key, so disjoint sub-key edits merge
      // instead of clobbering.
      if (values.customFields && reconcilesPerSubKey("customFields", values)) {
        const rebased = rebasedRecord(
          values.customFields,
          draft.baseline.customFields,
          serverForm.customFields,
        );
        if (rebased) {
          values.customFields = rebased;
        } else {
          delete values.customFields;
        }
      }
      // Mode comes from the PRE-prune values: when the server converges on
      // the switched-to model, priceModelId prunes away, and deciding from
      // the pruned copy would diff the new model's inputs against the old
      // model's baseline. The wholesale record carries over instead; both
      // sides target the same model after this rebase, so per-sub-key
      // reads are correct from then on.
      if (
        values.priceModelInputs &&
        reconcilesPerSubKey("priceModelInputs", draft.values)
      ) {
        const rebased = rebasedRecord(
          values.priceModelInputs,
          draft.baseline.priceModelInputs,
          serverForm.priceModelInputs,
        );
        if (rebased) {
          values.priceModelInputs = rebased;
        } else {
          delete values.priceModelInputs;
        }
      }
      const draftsByItemId = new Map(state.draftsByItemId);
      draftsByItemId.set(itemId, { baseline: serverForm, values });
      return { draftsByItemId };
    });
  },

  clearDraft: (itemId) => {
    set((state) => {
      if (!state.draftsByItemId.has(itemId)) {
        return {};
      }
      const draftsByItemId = new Map(state.draftsByItemId);
      draftsByItemId.delete(itemId);
      return { draftsByItemId };
    });
  },
}));

/** The full form as rendered: the user's edits overlaid on the server form. */
export function draftFormData(
  draft: ItemDraft | undefined,
  serverForm: ItemFormData,
): ItemFormData {
  if (!draft) {
    return serverForm;
  }
  const merged = { ...serverForm, ...draft.values };
  // Record keys render per sub-key: the server's untouched sub-keys show
  // through beneath the user's edited ones.
  if (
    draft.values.customFields &&
    reconcilesPerSubKey("customFields", draft.values)
  ) {
    merged.customFields = {
      ...serverForm.customFields,
      ...changedSubKeys(draft.values.customFields, draft.baseline.customFields),
    };
  }
  if (
    draft.values.priceModelInputs &&
    reconcilesPerSubKey("priceModelInputs", draft.values)
  ) {
    merged.priceModelInputs = {
      ...serverForm.priceModelInputs,
      ...changedSubKeys(
        draft.values.priceModelInputs,
        draft.baseline.priceModelInputs,
      ),
    };
  }
  return merged;
}

/** Keys the user has edited off the draft's baseline. */
export function draftDirtyKeys(
  draft: ItemDraft | undefined,
): (keyof ItemFormData)[] {
  if (!draft) {
    return [];
  }
  return (Object.keys(draft.values) as (keyof ItemFormData)[]).filter((key) => {
    if (!isRecordFieldKey(key)) {
      return true;
    }
    // A wholesale (model-switch) input replacement is always a real edit.
    if (!reconcilesPerSubKey(key, draft.values)) {
      return true;
    }
    return Object.keys(recordFieldEdits(draft, key)).length > 0;
  });
}

/** Dirty fields in true conflict with the server (per disagreesWithEdit). */
export function draftConflictKeys(
  draft: ItemDraft | undefined,
  serverForm: ItemFormData,
): (keyof ItemFormData)[] {
  if (!draft) {
    return [];
  }
  return draftDirtyKeys(draft).filter((key) =>
    isRecordFieldKey(key)
      ? recordFieldConflicts(draft, serverForm, key)
      : disagreesWithEdit(
          serverForm[key],
          draft.baseline[key],
          draft.values[key],
        ),
  );
}

/**
 * The user's per-sub-key edits for a record-valued draft key. Empty in
 * wholesale mode (a model switch replaces the inputs, not their sub-keys).
 */
export function recordFieldEdits(
  draft: ItemDraft | undefined,
  key: RecordFieldKey,
): Record<string, unknown> {
  if (!draft || !draft.values[key] || !reconcilesPerSubKey(key, draft.values)) {
    return {};
  }
  return changedSubKeys(draft.values[key] ?? {}, draft.baseline[key]);
}

/**
 * Per-sub-key conflict for a record key: a sub-key the user edited that the
 * server also moved elsewhere. A wholesale input replacement never conflicts
 * on its own — the priceModelId change carries any disagreement.
 */
function recordFieldConflicts(
  draft: ItemDraft,
  serverForm: ItemFormData,
  key: RecordFieldKey,
): boolean {
  const base: Record<string, unknown> = draft.baseline[key] ?? {};
  const theirs: Record<string, unknown> = serverForm[key] ?? {};
  if (!reconcilesPerSubKey(key, draft.values)) {
    // Wholesale: the model id usually carries the disagreement — but when
    // the server landed on the same model the ids agree, so the inputs
    // must carry it themselves.
    return (
      serverForm.priceModelId === draft.values.priceModelId &&
      disagreesWithEdit(theirs, base, draft.values[key])
    );
  }
  return Object.entries(recordFieldEdits(draft, key)).some(([subKey, mine]) =>
    disagreesWithEdit(theirs[subKey], base[subKey], mine),
  );
}

/**
 * True when the server moved a value off the baseline and it also differs
 * from the user's unsaved value — the only combination that is a conflict.
 */
function disagreesWithEdit(
  theirs: unknown,
  base: unknown,
  mine: unknown,
): boolean {
  return !isSameValue(theirs, base) && !isSameValue(theirs, mine);
}

/**
 * Whether a record key reconciles per sub-key. customFields always does;
 * priceModelInputs only while the draft keeps the baseline's price model —
 * switching models replaces the inputs wholesale.
 */
function reconcilesPerSubKey(
  key: RecordFieldKey,
  values: Partial<ItemFormData>,
): boolean {
  return key === "customFields" || !("priceModelId" in values);
}

function isRecordFieldKey(key: keyof ItemFormData): key is RecordFieldKey {
  return key === "customFields" || key === "priceModelInputs";
}

/**
 * Rebases an edited record value onto a newer server form: the server's
 * sub-keys with the user's changed sub-keys re-applied. Null when the merge
 * no longer differs from the server copy.
 */
function rebasedRecord<V>(
  mine: Record<string, V>,
  base: Record<string, V> | undefined,
  theirs: Record<string, V> | undefined,
): Record<string, V> | null {
  const theirValues = theirs ?? {};
  const edits = changedSubKeys(mine, base);
  const differs = Object.entries(edits).some(
    ([key, value]) => !isSameValue(value, theirValues[key]),
  );
  return differs ? { ...theirValues, ...edits } : null;
}

/** Sub-keys of `mine` changed off `base` — the user's edits within a record. */
function changedSubKeys<V>(
  mine: Record<string, V>,
  base: Record<string, V> | undefined,
): Record<string, V> {
  const baseValues = base ?? {};
  return Object.fromEntries(
    Object.entries(mine).filter(
      ([key, value]) => !isSameValue(value, baseValues[key]),
    ),
  );
}

/** Drops draft values the server now matches. */
function pruneMatchingValues(
  values: Partial<ItemFormData>,
  serverForm: ItemFormData,
): Partial<ItemFormData> {
  const pruned: Partial<ItemFormData> = {};
  const copyKeyIfDiffering = <K extends keyof ItemFormData>(key: K) => {
    if (!isSameValue(values[key], serverForm[key])) {
      pruned[key] = values[key];
    }
  };
  for (const key of Object.keys(values) as (keyof ItemFormData)[]) {
    copyKeyIfDiffering(key);
  }
  return pruned;
}

/** JSON-equality for form values, treating null and undefined alike. */
export function isSameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
